/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Shared utility for fetching all pages from a paginated API endpoint.
 * Safety-capped to prevent runaway loops and excessive DB costs.
 *
 * WARNING: Prefer server-side search (e.g., useMaterialsSearch) for large
 * datasets. This helper is appropriate only for small, bounded datasets
 * like cost categories, labor rates, and equipment rates where the total
 * count is reliably under a few hundred rows.
 */

import type { createApiClient } from "@/lib/api/api-client";
import { isApiFailure } from "@/lib/api/api-client";
import { logger } from "@/lib/utils/logger";

interface FetchAllPagesOptions {
  /** Maximum pages to fetch before truncating (default: 3) */
  maxPages?: number;
  /** Items per page (default: 100) */
  pageSize?: number;
}

/**
 * Fetch all pages from a paginated API endpoint.
 *
 * Uses the standard API client pagination contract: `res.pagination?.hasMore`.
 * Logs a warning when truncation occurs so callers can be identified for
 * migration to server-side search.
 */
export async function fetchAllPages<T>(
  api: ReturnType<typeof createApiClient>,
  endpoint: string,
  extraParams?: Record<string, string>,
  options?: FetchAllPagesOptions,
): Promise<T[]> {
  const { maxPages = 3, pageSize = 100 } = options ?? {};
  const all: T[] = [];
  let page = 1;
  let hasMore = true;

  while (hasMore && page <= maxPages) {
    const searchParams = new URLSearchParams();
    if (extraParams) {
      for (const [k, v] of Object.entries(extraParams)) {
        searchParams.set(k, v);
      }
    }
    searchParams.set("limit", String(pageSize));
    searchParams.set("page", String(page));

    const res = await api.get<T[]>(`${endpoint}?${searchParams.toString()}`);
    if (isApiFailure(res)) {
      throw res;
    }

    const data = Array.isArray(res.data) ? res.data : [];
    all.push(...data);
    hasMore = res.pagination?.hasMore === true;
    page++;
  }

  if (hasMore && page > maxPages) {
    logger.warn(
      `[fetchAllPages] Truncated at ${maxPages * pageSize} rows for ${endpoint} — switch caller to server-side search`,
      { endpoint, extraParams, fetched: all.length },
    );
  }

  return all;
}

/**
 * Fetch all pages with custom response extractors.
 *
 * Use this when the API response shape differs from the standard
 * `{ data: T[], pagination: { hasMore } }` contract.
 */
export async function fetchAllPagesCustom<T>(
  api: ReturnType<typeof createApiClient>,
  endpoint: string,
  baseParams: URLSearchParams,
  extractData: (response: unknown) => T[],
  extractPagination: (
    response: unknown,
  ) => { total: number; page: number; limit: number } | null,
  options?: FetchAllPagesOptions,
): Promise<T[]> {
  const { maxPages = 5, pageSize = 100 } = options ?? {};
  const allData: T[] = [];
  let page = 1;
  let hasMore = true;

  while (hasMore && page <= maxPages) {
    const params = new URLSearchParams(baseParams);
    params.set("page", page.toString());
    params.set("limit", pageSize.toString());

    const response = await api.get<unknown>(`${endpoint}?${params.toString()}`);

    if (isApiFailure(response)) {
      throw new Error(response.message || "Failed to fetch data");
    }

    const pageData = extractData(response.data);
    allData.push(...pageData);

    const pagination = extractPagination(response.data);
    if (pagination) {
      const totalPages = Math.ceil(pagination.total / pagination.limit);
      hasMore = page < totalPages && pageData.length === pageSize;
    } else {
      hasMore = pageData.length === pageSize;
    }

    page++;
  }

  if (hasMore && page > maxPages) {
    logger.warn(
      `[fetchAllPages] Truncated at ${maxPages * pageSize} rows for ${endpoint}`,
      { endpoint, fetched: allData.length },
    );
  }

  return allData;
}
